import { getBackendLanguage } from '@assets/i18n/config';
import type {
  AssetClassBranch,
  Strategy,
  TargetAllocation,
} from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import type { AllocationWeightSource } from '../addModelPortfoliosIPSAndAssetClasses/components/assetClassesTargetAllocations';

/** Shape of a `BASIC_FIELD` option as configured in workflow-service. */
export type IpsFieldOption = {
  enabled?: boolean;
  label?: TranslatedString;
  required?: string;
};

/**
 * What the household IPS steps are configured with. The editing step and the
 * recap read the same set so the two can be shown side by side.
 */
export type HouseholdIpsOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  ipsDescription?: IpsFieldOption;
  ipsInvestmentObjective?: IpsFieldOption;
  ipsLiquidity?: IpsFieldOption;
  ipsDisclaimer?: IpsFieldOption;
  enableAssetClassTargetAllocations?: boolean;
  assetClassTargetAllocationsLabel?: TranslatedString;
} & Record<string, any>;

/** The options that turn a single IPS field on and name it. */
export type HouseholdIpsFieldOptionKey =
  | 'ipsDescription'
  | 'ipsInvestmentObjective'
  | 'ipsLiquidity'
  | 'ipsDisclaimer';

/**
 * The parts of the step's configuration that say which IPS fields exist and
 * what they are called. Steps that only recap the IPS follow the set the
 * editing step was configured with, rather than being configured twice with
 * two answers that could disagree.
 */
export type HouseholdIpsFieldConfig = Pick<
  HouseholdIpsOptions,
  | HouseholdIpsFieldOptionKey
  | 'enableAssetClassTargetAllocations'
  | 'assetClassTargetAllocationsLabel'
>;

export const toIpsFieldConfig = (
  options: HouseholdIpsOptions,
): HouseholdIpsFieldConfig => ({
  ipsDescription: options?.ipsDescription,
  ipsInvestmentObjective: options?.ipsInvestmentObjective,
  ipsLiquidity: options?.ipsLiquidity,
  ipsDisclaimer: options?.ipsDisclaimer,
  enableAssetClassTargetAllocations: options?.enableAssetClassTargetAllocations,
  assetClassTargetAllocationsLabel: options?.assetClassTargetAllocationsLabel,
});

/** The IPS fields, named as they are on the model portfolio. */
export type HouseholdIpsValues = {
  translatedPortfolioDescription?: TranslatedString;
  translatedInvestmentObjective?: TranslatedString;
  translatedLiquidity?: TranslatedString;
  translatedPortfolioDisclaimer?: TranslatedString;
  targetAllocations?: TargetAllocation[];
};

/** The IPS fields edited as translated copy rather than allocation bands. */
export type HouseholdIpsNarrativeKey = Exclude<
  keyof HouseholdIpsValues,
  'targetAllocations'
>;

/**
 * The parts of a model the target allocation bands are compared against.
 * Asset-class-strategy models carry their weights on branches; every other
 * strategy carries them on the holdings.
 */
export type HouseholdIpsActuals = {
  strategy?: Strategy;
  securities?: AllocationWeightSource[];
  assetClassBranches?: AssetClassBranch[];
};

export type HouseholdIpsFieldDefinition = {
  optionKey: HouseholdIpsFieldOptionKey;
  valueKey: HouseholdIpsNarrativeKey;
  testId: string;
  richText: boolean;
};

/**
 * The plain-text fields, in display order. The disclaimer is kept apart
 * because it is configured the same way but written as rich text.
 */
export const IPS_TEXT_FIELDS = [
  {
    optionKey: 'ipsDescription',
    valueKey: 'translatedPortfolioDescription',
    testId: 'household-ips-description',
    richText: false,
  },
  {
    optionKey: 'ipsInvestmentObjective',
    valueKey: 'translatedInvestmentObjective',
    testId: 'household-ips-objective',
    richText: false,
  },
  {
    optionKey: 'ipsLiquidity',
    valueKey: 'translatedLiquidity',
    testId: 'household-ips-liquidity',
    richText: false,
  },
] as const satisfies readonly HouseholdIpsFieldDefinition[];

export const DISCLAIMER_FIELD = {
  optionKey: 'ipsDisclaimer',
  valueKey: 'translatedPortfolioDisclaimer',
  testId: 'household-ips-disclaimer',
  richText: true,
} as const satisfies HouseholdIpsFieldDefinition;

export const IPS_NARRATIVE_FIELDS: readonly HouseholdIpsFieldDefinition[] = [
  ...IPS_TEXT_FIELDS,
  DISCLAIMER_FIELD,
];

/**
 * An emptied rich text document still carries the markup it was typed into, so
 * a disclaimer counts as filled only once there is text left without the tags.
 */
export const hasContent = (
  value: string | undefined,
  richText: boolean,
): boolean =>
  richText
    ? !!value
        ?.replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim()
    : !!value;

/**
 * A field blocks the step only when it is on screen, configured as a hard
 * requirement, and empty in the language the backend stores.
 */
export const isMissing = (
  option: IpsFieldOption | undefined,
  value: TranslatedString | undefined,
  richText = false,
): boolean =>
  !!option?.enabled &&
  option.required === FieldRequiredType.ERROR &&
  !hasContent(value?.[getBackendLanguage()], richText);
